import _typeof from "@babel/runtime/helpers/typeof";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _get from "lodash/get";
import _cloneDeep from "lodash/cloneDeep";
import _map from "lodash/map";
import _set from "lodash/set";
import _has from "lodash/has";
import _isArray from "lodash/isArray";
import _isString from "lodash/isString";
import _isNumber from "lodash/isNumber";
import _isNull from "lodash/isNull";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.string.iterator.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.array.iterator.js";
import "core-js/modules/web.dom.iterable.js";
import "core-js/modules/es6.promise.js";
import ExtendableError from 'es6-error';
import stringifyNode from './stringify-node';
import pickWildcards from './pick-wildcards'; // TODO: reorganize validation related methods and errors
// from migrations and errors source files to this tree-utils file
// eslint-disable-next-line import/no-cycle

import { validateNode } from '../migrations';

var BreakWalkError = /*#__PURE__*/function (_ExtendableError) {
  _inherits(BreakWalkError, _ExtendableError);

  var _super = _createSuper(BreakWalkError);

  function BreakWalkError(node) {
    var _this;

    _classCallCheck(this, BreakWalkError);

    _this = _super.call(this, node);
    _this.message = "node walk broke at: ".concat(stringifyNode(node)); // eslint-disable-line no-use-before-define

    return _this;
  }

  return BreakWalkError;
}(ExtendableError); // this is source code from the `is-promise` package.


export function isPromise(obj) {
  return !!obj && (_typeof(obj) === 'object' || typeof obj === 'function') && typeof obj.then === 'function';
} // recurse all nodes in tree and perform an operation
// use the node returned from operation as the base for the next recursive operation

function transformNode(node, schema, iteratee) {
  var key = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';
  if (_isNull(node)) return node;
  if (_isNumber(node)) return node;
  if (_isString(node)) return node;

  if (_isArray(node)) {
    var output = node.map(function (n, i) {
      return transformNode(n, schema, iteratee, "".concat(key, "[").concat(i, "]"));
    }); // if an iteratee returned a promise, this whole method is async

    if (output.some(isPromise)) return Promise.all(output); // iteratee was always sync.  this method is sync

    return output;
  } // method to recurse into child nodes
  // after iteratee has run for this node
  // transforms all nodes in structured node props
  // declared in schema that exist on this node


  function transformNodeProps(tnode) {
    // `tnode` is the tranformed node returned from iteratee
    // sanity check
    if (!_has(tnode, 'type') || !_has(tnode, 'props') || !_has(schema, tnode.type)) {
      // something is wrong.  validate the node to get useful error message
      validateNode(tnode, key, schema);
    }

    var type = tnode.type,
        props = tnode.props;
    var nodeProps = schema[type].nodeProps;
    var propsKey = key ? "".concat(key, ".props") : 'props'; // case for start node is object vs. array
    // pick out all the structured node props declared in schema that exist on this node

    var propsToTransform = pickWildcards(props, nodeProps); // a map method to transform those props and set the transformed prop value on this node

    var transformNodeProp = function transformNodeProp(val, name) {
      // method to update prop value on node for either sync or async runs
      var updatePropValue = function updatePropValue(v) {
        return _set(tnode.props, name, v);
      }; // recurse down


      var prop = transformNode(val, schema, iteratee, "".concat(propsKey, ".").concat(name)); // set the output on the prop being transformed in the node
      // when recursion is async, wait to update

      if (isPromise(prop)) return prop.then(updatePropValue); // when recursion is sync, update immediately

      return updatePropValue(prop);
    }; // transform all matched node-props


    var output = _map(propsToTransform, transformNodeProp); // return the transformed node after all node-props have been transformed
    // when recursion is async, wait until all props are updated


    if (output.some(isPromise)) {
      return Promise.all(output).then(function () {
        return tnode;
      });
    } // when recursion is sync, immediately return transformed node


    return tnode;
  } // run the iteratee method to transform the node


  var transformed = iteratee(node, key, schema); // when this iteratee is async, wait for it to run
  // before recursing down into child nodes
  // if iteratee returns async at any level of recursion,
  // then the function-call at the top is async/returns a promise

  if (isPromise(transformed)) return transformed.then(transformNodeProps); // iteratee is synchronous. immediately recurse down.

  return transformNodeProps(transformed);
} // recurse through the tree and alter nodes with synchronous or asynchronous operations
// returns promise for transformed node when operations are async
// otherwise returns transformed node


export function transform(node, schema, iteratee) {
  var _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
      _ref$mutate = _ref.mutate,
      mutate = _ref$mutate === void 0 ? false : _ref$mutate;

  if (mutate === true) return transformNode(node, schema, iteratee);
  return transformNode(_cloneDeep(node), schema, iteratee);
} // recurse through the tree and perform a synchronous operation
// returns undefined

export default function walk(node, schema, iteratee) {
  var walker = function walker(n, key) {
    // assert node is valid to schema
    validateNode(n, key, schema); // run the supplied iteratee method

    if (iteratee(n, key, schema) === false) {
      // break the iteration if `false` was returned
      throw new BreakWalkError(node);
    } // return the original node, recurse down


    return n;
  };

  try {
    // run transform with `mutate: true` to avoid copying data
    transform(node, schema, walker, {
      mutate: true
    });
  } catch (err) {
    if (err instanceof BreakWalkError === false) {
      throw err;
    }
  }
}
/**
 * predicate takes an SC node as a parameter and returns true or false
 * based on whether it is the node that the user is looking for
 *
 * @callback nodePredicateFn
 * @param {Object} node
 * @returns {boolean}
 */

/**
 * Finds the first occurrence of a node based on a supplied predicate function
 *
 * @param {Object} doc
 * @param {Object} schema
 * @param {(nodePredicateFn|string)} predicate node => boolean, or the type of node to find as a string
 * @returns {(undefined|Object)} the first occurrence of the node (BFS) or undefined if none were found
 */

export function find(doc, schema, predicate) {
  var result;
  var predicateFn = typeof predicate === 'string' ? function (node) {
    return node.type === predicate;
  } : predicate;
  walk(doc, schema, function (node) {
    if (predicateFn(node)) {
      result = node;
      throw new BreakWalkError();
    }
  });
  return result;
}
/**
 * Finds multiple nodes based on a supplied predicate function
 *
 * @param {Object} doc
 * @param {Object} schema
 * @param {nodePredicateFn} predicate node => boolean
 * @returns {(Object[])} zero or more found nodes
 */

export function filter(doc, schema, predicate) {
  var results = [];
  var predicateFn = typeof predicate === 'string' ? function (node) {
    return node.type === predicate;
  } : predicate;
  walk(doc, schema, function (node) {
    if (predicateFn(node)) {
      results.push(node);
    }
  });
  return results;
} // lookup node with keypath provided when walking node

export function get(node, key) {
  if (!key) return node;
  return _get(node, key);
}
export function each(node, schema, iteratee) {
  var i = 0; // eslint-disable-next-line no-shadow

  walk(node, schema, function (node, key, schema) {
    var out = iteratee(node, i, schema);
    i += 1;
    return out;
  });
  return node;
} // walks the tree, and maps every node into a flat list

export function map(node, schema) {
  var iteratee = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (n) {
    return n;
  };
  var out = [];
  walk(node, schema, function (n, key) {
    return out.push(iteratee(n, key));
  });
  return out;
} // map the tree, return whole nodes

export function toArray(node, schema) {
  return map(node, schema, function (n) {
    return n;
  });
}